// use-query-keys.ts
// This file provides the useQueryKeys hook for generating query keys for React Query based on context and arguments.
// Useful for cache management and query invalidation in the application.

import { ProcArgs, ProceduresQueryKey } from "@/types/procedure";
import { useOrdersContext } from "@/contexts/orders-context";
import { useAppState } from "@/contexts/app-state";

// "orders" for now but this will be union of string literals
type QueryKeyTypes = "orders";

/**
 * useQueryKeys Hook
 * Generates query keys for React Query based on the provided type and arguments, using context for pagination and sorting.
 *
 * @param type - The type of query key to generate (e.g., "orders")
 * @returns A function that generates a query key array for React Query
 *
 * @example
 * const getQueryKey = useQueryKeys("orders");
 * const queryKey = getQueryKey({ user, filters, bucketName });
 */
export const useQueryKeys = (type: QueryKeyTypes) => {
  const { pageNumber, sortCriteria, sortOrder } = useOrdersContext();
  const { context } = useAppState();
  const practiceId = context?.athena?.practiceId ?? "8363";
  const departmentId = context?.athena?.departmentId ?? "3089";

  if (type === "orders")
    return ({
      user,
      filters,
      bucketName,
    }: Pick<ProcArgs, "user" | "filters" | "bucketName">): ProceduresQueryKey => [
        "orders",
        user,
        {
          filters,
          pageNumber,
          pageSize: 20,
          practiceId,
          departmentId,
          bucketName,
          sortCriteria,
          sortOrder
        },
      ];

  const _notgonnahappen: never = type;
  return _notgonnahappen;
};
